import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CallWithAuth } from '../../../actions/apiAction';
import {
    GET_SUBSCRIPTION_PLANS,
    GET_MESSAGE_LIMITS,
    GET_CREATOR_DETAILS,
    GET_SUBSCRIBED_CREATOR_BY_ID,
    CREATE_CHECKOUT_SESSION,
    RESUME_SUBSCRIPTION_BY_ID,
} from '../../../utils/apiUrl';

// A trial is considered "low" once any capped limit has 20% or less remaining,
// and "exhausted" once every capped limit has hit zero. Limits that are null
// are unlimited and never count against either state.
const getTrialStatus = (limits) => {
    if (!limits) return 'unknown';

    const pairs = [
        [limits.text_message_limit, limits.text_messages_remaining],
        [limits.voice_minutes_limit, limits.voice_minutes_remaining],
        [limits.video_minutes_limit, limits.video_minutes_remaining],
    ].filter(([limit]) => limit !== null && limit !== undefined && limit > 0);

    if (pairs.length === 0) return 'ok';

    const allExhausted = pairs.every(([, remaining]) => (remaining ?? 0) <= 0);
    if (allExhausted) return 'exhausted';

    const anyLow = pairs.some(([limit, remaining]) => (remaining ?? 0) / limit <= 0.2);
    if (anyLow) return 'low';

    return 'ok';
};

const formatMessageCount = (value) => {
    const count = Number(value);
    if (Number.isNaN(count)) return '0';
    return String(Math.max(0, Math.floor(count)));
};

const formatMinutes = (value) => {
    const minutes = Number(value);
    if (Number.isNaN(minutes)) return '0';
    return String(Math.max(0, Math.round(minutes)));
};

const formatShortDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function Plans() {
    const navigate = useNavigate();
    const { search } = useLocation();
    const query = useMemo(() => new URLSearchParams(search), [search]);
    const creatorId = query.get('id');

    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [addonPlans, setAddonPlans] = useState([]);
    const [creatorDetails, setCreatorDetails] = useState(null);
    const [subscriptionLimit, setSubscriptionLimit] = useState(null);
    const [subscriptionDetail, setSubscriptionDetail] = useState(null);
    const [actionLoadingPlanId, setActionLoadingPlanId] = useState(null);

    const fetchData = async ({ showLoader } = { showLoader: true }) => {
        if (!creatorId) {
            setLoading(false);
            return;
        }
        if (showLoader) setLoading(true);

        const [plansRes, addonsRes, limitsRes, creatorRes, subRes] = await Promise.all([
            CallWithAuth('GET', GET_SUBSCRIPTION_PLANS(creatorId)),
            CallWithAuth('GET', GET_SUBSCRIPTION_PLANS(creatorId, 'add-on')),
            CallWithAuth('GET', GET_MESSAGE_LIMITS(creatorId)),
            CallWithAuth('GET', GET_CREATOR_DETAILS(creatorId)),
            CallWithAuth('GET', GET_SUBSCRIBED_CREATOR_BY_ID(creatorId)),
        ]);

        if (plansRes?.data) setPlans(plansRes.data);
        if (addonsRes?.data) setAddonPlans(Array.isArray(addonsRes.data) ? addonsRes.data : []);
        if (limitsRes?.data) setSubscriptionLimit(limitsRes.data);
        if (creatorRes?.data) setCreatorDetails(creatorRes.data);
        // 404 just means the user has never subscribed to this creator.
        setSubscriptionDetail(subRes?.status === 200 ? subRes.data : null);

        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatorId]);

    // The creator hasn't finished payout onboarding, so checkout would reject
    // this purchase. Show the plans as a preview instead of letting the fan tap
    // through to a failure. Only an explicit `false` locks — if the field is
    // missing (older API), fall through to the existing behaviour.
    const comingSoon = creatorDetails?.accepting_subscriptions === false;

    const createCheckoutSession = async (planId) => {
        if (!creatorId || comingSoon) return;
        setActionLoadingPlanId(planId);
        try {
            const response = await CallWithAuth('POST', CREATE_CHECKOUT_SESSION, {
                plan_id: planId,
                creator_id: creatorId,
            });

            if (!response) {
                toast.error("Couldn't reach the server. Please check your connection and try again.");
                return;
            }

            if (response.status === 200 && response?.data?.checkout_url) {
                window.location.href = response.data.checkout_url;
                return;
            }

            toast.error(response?.data?.detail || 'Something went wrong!');
        } finally {
            setActionLoadingPlanId(null);
        }
    };

    const reactivatePlan = async (subscriptionId, planId) => {
        if (!subscriptionId) return;
        setActionLoadingPlanId(planId);
        try {
            const response = await CallWithAuth('POST', RESUME_SUBSCRIPTION_BY_ID(subscriptionId));

            if (!response) {
                toast.error("Couldn't reach the server. Please check your connection and try again.");
                return;
            }

            if (response.status === 200) {
                toast.success('Subscription reactivated');
                await fetchData({ showLoader: false });
                return;
            }

            toast.error(response?.data?.detail || 'Something went wrong!');
            // The backend reconciles subscriptions it finds are no longer valid in
            // Stripe (e.g. 404 "no longer exists"), so refresh to reflect that here
            // too instead of leaving a stale "Reactivate" button in place.
            await fetchData({ showLoader: false });
        } finally {
            setActionLoadingPlanId(null);
        }
    };

    const creatorName = creatorDetails?.first_name || 'this creator';
    const trialStatus = getTrialStatus(subscriptionLimit);
    const isSubscribed = subscriptionLimit?.user_type === 'subscribed';
    const currentPlan = plans.find((p) => p.is_current_plan);
    // Mirrors the backend's access_granting_filter: "cancelled" still counts as
    // subscribed until current_period_end actually passes.
    const hasAnySubscription = Boolean(
        subscriptionDetail && (
            subscriptionDetail.status === 'active' ||
            (subscriptionDetail.status === 'cancelled' && (
                !subscriptionDetail.current_period_end ||
                new Date(subscriptionDetail.current_period_end) > new Date()
            ))
        )
    );
    const currentPlanEndsSoon = hasAnySubscription && subscriptionDetail?.status === 'cancelled';
    const currentPlanEndDate = formatShortDate(subscriptionDetail?.current_period_end);

    let headline = `Upgrade your time with ${creatorName}`;
    let subheadline = 'Choose a plan to keep your connection going';

    if (isSubscribed) {
        headline = `You're subscribed to ${creatorName}`;
        subheadline = currentPlanEndsSoon
            ? `Your ${currentPlan?.name || 'current'} plan ends${currentPlanEndDate ? ` on ${currentPlanEndDate}` : ''} — reactivate anytime before then`
            : currentPlan
                ? `Your ${currentPlan.name} plan is active — manage it anytime below`
                : 'Manage your plan below';
    } else if (trialStatus === 'exhausted') {
        headline = `Your free time with ${creatorName} is complete`;
        subheadline = 'Choose a plan to keep your connection going';
    } else if (trialStatus === 'low') {
        headline = `You're almost out of free time with ${creatorName}`;
        subheadline = 'Upgrade now to avoid any interruption';
    }

    const showTrialUsage = !isSubscribed && (trialStatus === 'low' || trialStatus === 'exhausted');

    return (
        <>
            <Helmet>
                <title>Subscription Plans | TLK</title>
            </Helmet>
            <div className="relative min-h-screen gradient-bg-1 p-4 pb-24 md:pb-6 md:p-6">
                <button
                    type="button"
                    className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/35 text-white flex items-center justify-center z-10"
                    onClick={() => window.location.href = '/'}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="m15 18-6-6 6-6" />
                    </svg>
                </button>
                <div
                    className="w-full max-w-none xl:max-w-5xl mx-auto"
                    style={{ opacity: 1, transform: 'none' }}
                >
                    {!creatorId ? (
                        <div className="text-center py-16">
                            <p className="text-foreground/70">No creator selected.</p>
                            <button
                                className="mt-4 underline text-primary"
                                onClick={() => navigate('/user/creator-list')}
                            >
                                Back to creators
                            </button>
                        </div>
                    ) : loading ? (
                        <div className="flex justify-center py-24">
                            <div className="loaderBox" style={{ position: 'unset', margin: 0, padding: 0, height: 'unset', width: 'unset' }}>
                                <div className="loader" style={{ width: 36, height: 36 }} />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="text-center mb-4 md:mb-6">
                                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                    {headline}
                                </h1>
                                <p className="text-white text-sm md:text-base mb-3">
                                    {subheadline}
                                </p>
                                {showTrialUsage && (
                                    <div className="bg-card rounded-2xl p-3 md:p-4 text-sm border border-border shadow-md inline-block">
                                        <p className="text-muted-foreground mb-1">
                                            {trialStatus === 'exhausted' ? "You've used your free trial:" : "You're running low on your free trial:"}
                                        </p>
                                        <div className="flex justify-center gap-6">
                                            {subscriptionLimit?.text_message_limit != null && (
                                                <div>
                                                    <span className="text-primary font-bold">
                                                        {formatMessageCount(subscriptionLimit.text_messages_remaining)}
                                                    </span>
                                                    <span className="text-foreground/70"> messages left</span>
                                                </div>
                                            )}
                                            {subscriptionLimit?.voice_minutes_limit != null && (
                                                <div>
                                                    <span className="text-primary font-bold">
                                                        {formatMinutes(subscriptionLimit.voice_minutes_remaining)}
                                                    </span>
                                                    <span className="text-foreground/70"> voice min left</span>
                                                </div>
                                            )}
                                            {subscriptionLimit?.video_minutes_limit != null && (
                                                <div>
                                                    <span className="text-primary font-bold">
                                                        {formatMinutes(subscriptionLimit.video_minutes_remaining)}
                                                    </span>
                                                    <span className="text-foreground/70"> video min left</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {comingSoon && plans.length > 0 && (
                                <div className="rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 mb-4 text-center">
                                    {/* first-letter:uppercase covers the 'this creator' fallback */}
                                    <p className="text-sm font-semibold text-white first-letter:uppercase">
                                        {creatorName} is getting set up
                                    </p>
                                    <p className="text-xs text-muted-white mt-1">
                                        Subscriptions open soon — thanks for your patience!
                                    </p>
                                </div>
                            )}

                            {plans.length === 0 ? (
                                <p className="text-center text-white py-12">
                                    No subscription plans are available for this creator yet.
                                </p>
                            ) : (
                                <div className="flex flex-col md:flex-row justify-center items-stretch gap-4 md:flex-wrap mb-6">
                                    {plans.map((plan) => {
                                        const isPremium = plan.name === 'Premium';
                                        const isCurrent = plan.is_current_plan;
                                        const isEndingSoon = isCurrent && currentPlanEndsSoon;
                                        const isBusy = actionLoadingPlanId === plan.id;
                                        const isUpgrade = Boolean(currentPlan) && Number(plan.price) > Number(currentPlan.price);

                                        return (
                                            <div
                                                key={plan.id}
                                                className={`${
                                                    isCurrent
                                                        ? isEndingSoon
                                                            ? 'upgrade-plan is-ending'
                                                            : 'upgrade-plan is-active'
                                                        : isPremium
                                                            ? 'upgrade-plan is-premium'
                                                            : 'upgrade-plan'
                                                } w-full max-w-md mx-auto`}
                                                style={{ opacity: 1, transform: 'none' }}
                                            >
                                                {isCurrent && (
                                                    <div className={isEndingSoon ? "absolute -top-4 left-2 md:left-1/2 transform md:-translate-x-1/2 bg-amber-500 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 shadow-lg" : "absolute -top-4 left-2 md:left-1/2 transform md:-translate-x-1/2 bg-primary text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 shadow-lg"}>
                                                        {isEndingSoon
                                                            ? `Active${currentPlanEndDate ? ` till ${currentPlanEndDate}` : ''}`
                                                            : 'Active'}
                                                    </div>
                                                )}
                                                {!isCurrent && plan.best_value && (
                                                    <div className="absolute -top-3 left-4 bg-primary text-white px-4 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width={24}
                                                            height={24}
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="#fff"
                                                            strokeWidth={2}
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="lucide lucide-star w-3 h-3"
                                                        >
                                                            <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                                                        </svg>
                                                        Best Value
                                                    </div>
                                                )}
                                                {!isCurrent && plan.discount_percentage ? (
                                                    <div className="absolute -top-3 right-4 bg-success text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                                                        {plan.discount_percentage}% OFF
                                                    </div>
                                                ) : null}

                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <h3 className={isPremium ? 'font-bold text-white text-2xl' : 'font-bold text-white text-xl'}>
                                                            {plan.name}
                                                        </h3>
                                                        <p className="text-xs text-muted-white">{plan.description}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        {plan.slashed_price ? (
                                                            <p className="text-sm text-muted-white line-through">
                                                                ${Number(plan.slashed_price).toFixed(2)}
                                                            </p>
                                                        ) : null}
                                                        <p className={isPremium ? 'font-bold text-white text-3xl text-primary' : 'font-bold text-white text-2xl'}>
                                                            ${Number(plan.price).toFixed(2)}
                                                        </p>
                                                        <p className="text-xs text-muted-white">{plan.interval === 'one_time' ? 'one-time' : plan.interval}</p>
                                                    </div>
                                                </div>

                                                <div className={isPremium ? 'space-y-1 text-foreground/80 mb-3 flex-grow text-base' : 'space-y-1 text-foreground/80 mb-3 flex-grow text-sm'}>
                                                    {plan.marketing_features?.length > 0 ? (
                                                        plan.marketing_features.map((feature, idx) => (
                                                            <p key={idx} className="flex items-center gap-2">• {feature}</p>
                                                        ))
                                                    ) : (
                                                        <>
                                                            <p>• {plan.limits?.text_messages ?? 'Unlimited'} texts</p>
                                                            <p>• {plan.limits?.voice_minutes ?? 'Unlimited'} voice minutes</p>
                                                            <p>• {plan.limits?.video_minutes ?? 'Unlimited'} video minutes</p>
                                                        </>
                                                    )}
                                                </div>

                                                {isCurrent ? (
                                                    isEndingSoon ? (
                                                        <button
                                                            disabled={isBusy}
                                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-primary text-primary-foreground hover:opacity-90 transition-all duration-300 disabled:opacity-50"
                                                            onClick={() => reactivatePlan(subscriptionDetail?.subscription_id, plan.id)}
                                                        >
                                                            {isBusy ? 'Reactivating…' : 'Reactivate'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-primary text-success border border-success/40 shadow-sm shadow-success/20 hover:bg-success/25 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                                                            onClick={() => navigate(`/user/billing-history?creator_id=${creatorId}`)}
                                                        >
                                                            Manage
                                                        </button>
                                                    )
                                                ) : hasAnySubscription ? (
                                                    <button
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-white text-primary border border-primary/40 shadow-sm shadow-primary/20 hover:bg-primary/10 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                                                        onClick={() => navigate(`/user/billing-history?creator_id=${creatorId}`)}
                                                    >
                                                        {isUpgrade ? 'Upgrade' : 'Manage Subscription'}
                                                    </button>
                                                ) : comingSoon ? (
                                                    <button
                                                        disabled
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-white/10 text-white/70 border border-white/20 cursor-not-allowed"
                                                    >
                                                        🔒 Coming soon
                                                    </button>
                                                ) : (
                                                    <button
                                                        disabled={isBusy}
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-primary text-primary-foreground hover:opacity-90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 w-full"
                                                        onClick={() => createCheckoutSession(plan.id)}
                                                    >
                                                        {isBusy ? 'Redirecting…' : isPremium ? 'Get Premium' : `Continue with ${plan.name}`}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {hasAnySubscription && addonPlans.length > 0 && !comingSoon && (
                                <div className="mt-8 mb-6">
                                    <h2 className="text-xl font-bold text-white text-center mb-2">
                                        Need more time with {creatorName}?
                                    </h2>
                                    <p className="text-sm text-white/70 text-center mb-4">
                                        Get extra voice and video time for this billing period
                                    </p>
                                    <div className="w-full max-w-md mx-auto">
                                        {addonPlans.map((pack) => {
                                            const isBusy = actionLoadingPlanId === pack.id;
                                            return (
                                                <div
                                                    key={pack.id}
                                                    className="upgrade-plan flex flex-col w-full"
                                                    style={{ opacity: 1, transform: 'none' }}
                                                >
                                                    <div className="flex items-start justify-between gap-4 mb-5">
                                                        <h3 className="font-bold text-white text-lg leading-snug flex-1 min-w-0">
                                                            {pack.name}
                                                        </h3>
                                                        <div className="text-right shrink-0">
                                                            <p className="font-bold text-white text-2xl leading-none text-primary">
                                                                ${Number(pack.price).toFixed(2)}
                                                            </p>
                                                            <p className="text-xs text-muted-white mt-1">
                                                                {pack.interval === 'one_time' ? 'one-time' : pack.interval}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        disabled={isBusy}
                                                        className="mt-auto inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-primary/90 h-10 bg-primary text-primary-foreground hover:opacity-90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 w-full"
                                                        onClick={() => createCheckoutSession(pack.id)}
                                                    >
                                                        {isBusy ? 'Redirecting…' : 'Get'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-center text-white px-6 mt-4">
                                {comingSoon ? (
                                    <>
                                        We'll let you know as soon as {creatorName} is ready.
                                        Nothing has been charged.
                                    </>
                                ) : (
                                    <>
                                        By continuing, you'll unlock continued access to {creatorName}. You can cancel
                                        anytime from your account settings.
                                    </>
                                )}
                            </p>
                            <p className="text-xs text-center text-white mt-2 pb-4">
                                <button className="underline hover:text-white transition-colors">
                                    Terms of Service
                                </button>{' '}
                                ·{' '}
                                <button className="underline hover:text-white transition-colors">
                                    Privacy Policy
                                </button>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
